import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import decode from "jwt-decode";
import { useTranslation } from "react-i18next";
import lngs from "../../lngs";
import MenuAppBar from "../Menu/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  InputBase,
  Badge,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Button,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Bell,
  HelpCircle,
  User,
  Lock,
  LogOut,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from "lucide-react";
import useAccountProfile from "../../hooks/useAccountProfile";

const Header = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };

  const profileOpen = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  const languageOpen = Boolean(languageAnchorEl);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage;
  const isApp = location.pathname.toLowerCase().includes("/app/");
  const { user, displayName, displayEmail, avatarLetter, avatarSrc } =
    useAccountProfile({ fetchProfile: isApp });
  const [isFullscreen, setIsFullscreen] = useState(
    document.fullscreenElement !== null
  );
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark";
  });

  const logout = useCallback(() => {
    dispatch({ type: "LOGOUT" });
    history.push(`/${currentLang}/login`);
    localStorage.removeItem("profile");
  }, [dispatch, history, currentLang]);

  function toggleFullScreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }

  function toggleDarkMode() {
    setIsDarkMode((prevMode) => {
      const newMode = !prevMode;
      const theme = newMode ? "dark" : "light";
      localStorage.setItem("theme", theme);
      document.body.className = theme;
      return newMode;
    });
  }

  // Apply saved theme on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "light";
    document.body.className = savedTheme;
  }, []);

  useEffect(() => {
    if (
      !user &&
      isApp &&
      !location.pathname.toLowerCase().includes("/login") &&
      !location.pathname.toLowerCase().includes("/app/invoice/")
    ) {
      history.push(`/${currentLang}/login`);
    }
  }, [user, location.pathname, currentLang, history, isApp]);

  // Check if we're on the ModernPOS terminal route and hide header
  const isModernPOSTerminal = location.pathname
    .toLowerCase()
    .includes("/app/pos/terminal/");

  if (isModernPOSTerminal) {
    return null; // Hide the header completely for ModernPOS terminal
  }

  const openLink = (link) => {
    history.push(`${link}`);
  };

  if (!isApp) return <MenuAppBar />;

  const handleLanguageChange = (languageCode, event) => {
    event.preventDefault();
    const currentPath = location.pathname;
    const regex = /^\/[a-z]{2}(?=\/|$)/;
    const newPath = currentPath.replace(regex, `/${languageCode}`);
    history.replace(newPath);
    i18n.changeLanguage(languageCode);
  };
  if (!user) {
    return null;
  }
  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        className="np"
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            height: "44px",
          }}
        >
          {/* Left side - Logo + Nav controls */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Company Logo/Name */}
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                display: { xs: "none", sm: "block" },
                background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginLeft: "5px",
                marginRight: "5px",
              }}
            >
              Quantum Billing ERP
            </Typography>

          </Box>

          {/* Search */}
          <Box
            sx={{
              position: "relative",
              ml: 2,
              mr: 2,
              flexGrow: { xs: 0, md: 0.4 },
              display: { xs: "none", md: "block" },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                display: "flex",
                alignItems: "center",
                height: "100%",
                pl: 1,
                pointerEvents: "none",
                color: "text.secondary",
              }}
            >
              <SearchIcon size={18} />
            </Box>
            <InputBase
              placeholder={t("search")}
              sx={{
                width: "100%",
                height: "36px",
                fontSize: "0.875rem",
                pl: 4,
                pr: 1,
                bgcolor: "background.default",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                "& .MuiInputBase-input": {
                  padding: "8px 0",
                },
              }}
            />
          </Box>

          {/* Right side - Actions and Profile */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Back / Forward / Reload */}
            <Tooltip title={t("back")}>
              <IconButton size="small" onClick={() => history.goBack()}>
                <ChevronLeft size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("forward")}>
              <IconButton size="small" onClick={() => history.goForward()}>
                <ChevronRight size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("reload")}>
              <IconButton size="small" onClick={() => window.location.reload()}>
                <RotateCcw size={18} />
              </IconButton>
            </Tooltip>

            {/* Language Selector */}
            <Tooltip title="Change Language">
              <IconButton
                color="inherit"
                size="medium"
                sx={{ ml: 1 }}
                onClick={handleLanguageMenuOpen}
                aria-controls={languageOpen ? "language-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={languageOpen ? "true" : undefined}
              >
                <LanguageIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                size="medium"
                onClick={handleNotificationMenuOpen}
                aria-controls={
                  notificationOpen ? "notification-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={notificationOpen ? "true" : undefined}
                sx={{ ml: 1 }}
              >
                <Badge badgeContent={4} color="error">
                  <Bell size={20} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Messages">
              <IconButton
                color="inherit"
                size="medium"
                sx={{ ml: 1 }}
                onClick={() => {
                  openLink(`/${currentLang}/app/messaging`);
                }}
              >
                <Badge badgeContent={0} color="error">
                  <MessageCircle size={20} style={{ color: "#4CAF50" }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Help">
              <IconButton color="inherit" size="medium" sx={{ ml: 1 }}>
                <HelpCircle size={20} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Toggle Fullscreen">
              <IconButton
                color="inherit"
                size="medium"
                sx={{ ml: 1 }}
                onClick={toggleFullScreen}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              <IconButton
                color="inherit"
                size="medium"
                sx={{ ml: 1 }}
                onClick={toggleDarkMode}
              >
                {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>

            {/* Profile Menu */}
            <Box sx={{ ml: 2 }}>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  aria-controls={profileOpen ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={profileOpen ? "true" : undefined}
                >
                  <Avatar
                    src={avatarSrc}
                    alt={displayName || displayEmail || t("account")}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "primary.main",
                      fontSize: "0.875rem",
                    }}
                  >
                    {avatarLetter}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>

            {/* Profile Menu Items */}
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={profileOpen}
              onClose={handleProfileMenuClose}
              onClick={handleProfileMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
                  mt: 1.5,
                  width: 200,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={() => openLink(`/${currentLang}/profile`)}>
                <ListItemIcon>
                  <User size={18} />
                </ListItemIcon>
                {t("profile")}
              </MenuItem>
              <MenuItem onClick={() => openLink(`/${currentLang}/change-password`)}>
                <ListItemIcon>
                  <Lock size={18} />
                </ListItemIcon>
                {t("change_password")}
              </MenuItem>
              <Divider />
              <MenuItem onClick={logout}>
                <ListItemIcon>
                  <LogOut size={18} />
                </ListItemIcon>
                {t("logout")}
              </MenuItem>
            </Menu>

            {/* Notification Menu */}
            <Menu
              anchorEl={notificationAnchorEl}
              id="notification-menu"
              open={notificationOpen}
              onClose={handleNotificationMenuClose}
              onClick={handleNotificationMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
                  mt: 1.5,
                  width: 320,
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                  {t("notifications")}
                </Typography>
                <Divider />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("no_new_notifications")}
                  </Typography>
                </Box>
              </Box>
            </Menu>

            {/* Language Menu */}
            <Menu
              anchorEl={languageAnchorEl}
              id="language-menu"
              open={languageOpen}
              onClose={handleLanguageMenuClose}
              onClick={handleLanguageMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
                  mt: 1.5,
                  width: 360,
                  maxWidth: "calc(100vw - 32px)",
                  p: 1,
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 0.5,
                }}
              >
                {Object.keys(lngs).map((lng) => (
                  <MenuItem
                    key={lng}
                    onClick={() => {
                      i18n.changeLanguage(lng);
                      const currentPath = location.pathname;
                      const newPath = currentPath.replace(
                        `/${currentLang}/`,
                        `/${lng}/`
                      );
                      history.push(newPath);
                    }}
                    selected={i18n.resolvedLanguage === lng}
                    sx={{
                      py: 1.25,
                      px: 1.5,
                      minHeight: 42,
                      borderRadius: 1,
                      whiteSpace: "normal",
                    }}
                  >
                    {lngs[lng].nativeName}
                  </MenuItem>
                ))}
              </Box>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

    </>
  );
};

export default Header;
